import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import VmsPageCard from "../components/VmsPageCard";
import { vmsApi } from "../services/vmsApi";
import { isVmsGeneralAdmin } from "../utils/auth";
import { formatSignedOutBy, getVmsStatusBadge, isPassNotValidForEntry } from "../utils/vmsStatus";

const VmsReportsVisits = () => {
  const isAdmin = isVmsGeneralAdmin();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    office_id: "",
    person_id: "",
    status: "",
    start_date: "",
    end_date: "",
    view: "lean",
    page: 1,
    page_size: 20,
    format: "csv",
  });
  const [meta, setMeta] = useState({
    total_count: 0,
    page: 1,
    page_size: 20,
    total_pages: 1,
    view: "lean",
  });
  const [offices, setOffices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const detailsDrawer = useDisclosure();
  const filtersDrawer = useDisclosure();
  const toast = useToast();

  const toIsoIfProvided = (value) => (value ? new Date(value).toISOString() : "");
  const toLocalTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday ? `Today, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : d.toLocaleString();
  };
  const getVisitorName = (row) => row?.visitor?.full_name || row?.visitor_name || "-";
  const getVisitorImage = (row) => row?.visitor?.image || undefined;
  const getMeetingName = (row) => row?.person_to_meet?.full_name || row?.person_to_meet_name || row?.person_to_meet || "-";
  const getOfficeName = (row) => row?.office?.name || row?.office_name || "-";
  const getRequestedAt = (row) => row?.timestamps?.requested_visit_time || row?.requested_visit_time || row?.created_at;
  const getSignedInAt = (row) => row?.timestamps?.signed_in_at || row?.signed_in_at;
  const getSignedOutAt = (row) => row?.timestamps?.signed_out_at || row?.signed_out_at;
  const getPurpose = (row) => row?.visitor?.visit_purpose || row?.visit_purpose || row?.purpose || "-";
  const getPhone = (row) => row?.visitor?.phone_number || row?.phone_number || "-";
  const getEmail = (row) => row?.visitor?.email || row?.email || "-";
  const getAddress = (row) => row?.visitor?.address || row?.address || "-";
  const getIdentification = (row) => row?.visitor?.identification || row?.identification || "-";
  const getPassGenerated = (row) =>
    typeof row?.card?.pass_generated === "boolean" ? row.card.pass_generated : Boolean(row?.pass_code);
  const getPassCode = (row) => row?.card?.pass_code || row?.pass_code || "—";
  const getPassValidForGateLabel = (row) => {
    if (isPassNotValidForEntry(row?.status)) return "No";
    if (!getPassGenerated(row)) return "—";
    return "Yes";
  };
  const getDuration = (row) => row?.computed?.visit_duration_hours ?? row?.visit_duration_hours ?? null;
  const openDetails = (row) => {
    setSelectedVisit(row);
    detailsDrawer.onOpen();
  };
  const openImagePreview = (imageUrl) => {
    if (!imageUrl) return;
    setPreviewImageUrl(imageUrl);
  };
  const [searchTerm, setSearchTerm] = useState("");

  const loadData = async (nextFilters = filters) => {
    setIsLoading(true);
    try {
      const payload = {
        ...nextFilters,
        ...(nextFilters.start_date ? { start_date: toIsoIfProvided(nextFilters.start_date) } : {}),
        ...(nextFilters.end_date ? { end_date: toIsoIfProvided(nextFilters.end_date) } : {}),
      };
      const data = await vmsApi.getVisitsReport(payload);
      const nextRows =
        data?.records ||
        data?.visits ||
        data?.requests ||
        data?.data ||
        (Array.isArray(data) ? data : []);

      setRows(Array.isArray(nextRows) ? nextRows : []);
      setMeta({
        total_count: data?.total_count ?? (Array.isArray(nextRows) ? nextRows.length : 0),
        page: data?.page ?? Number(nextFilters.page || 1),
        page_size: data?.page_size ?? Number(nextFilters.page_size || 20),
        total_pages: data?.total_pages ?? 1,
        view: data?.view ?? (nextFilters.view || "lean"),
      });
    } catch (err) {
      toast({ title: "Failed to load visits report", description: err?.message, status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const visitor = (row?.visitor?.full_name || row?.visitor_name || "").toLowerCase();
      const meeting = (row?.person_to_meet?.full_name || row?.person_to_meet_name || "").toLowerCase();
      const office = (row?.office?.name || row?.office_name || "").toLowerCase();
      const code = (row?.request_code || row?.id || "").toString().toLowerCase();
      return visitor.includes(term) || meeting.includes(term) || office.includes(term) || code.includes(term);
    });
  }, [rows, searchTerm]);

  useEffect(() => {
    const init = async () => {
      const officeData = await vmsApi.getOffices();
      setOffices(Array.isArray(officeData) ? officeData : officeData?.data || []);
      await loadData();
    };
    init().catch(() => {});
  }, []);

  const downloadTextFile = (text, filename, mimeType) => {
    const blob = new Blob([text], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const exportParams = {
        office_id: filters.office_id,
        person_id: filters.person_id,
        status: filters.status,
        start_date: filters.start_date ? toIsoIfProvided(filters.start_date) : "",
        end_date: filters.end_date ? toIsoIfProvided(filters.end_date) : "",
        view: filters.view,
        page: filters.page,
        page_size: filters.page_size,
        format: filters.format,
      };

      const result = await vmsApi.getExportReport(exportParams);

      if (typeof result === "string") {
        const ext = filters.format === "json" ? "json" : "csv";
        downloadTextFile(result, `vms-reports.${ext}`, ext === "json" ? "application/json" : "text/csv");
      } else if (result?.content !== undefined) {
        const contentAsText =
          typeof result.content === "string" ? result.content : JSON.stringify(result.content, null, 2);
        const ext = result.format || filters.format || "json";
        downloadTextFile(
          contentAsText,
          result.filename || `vms-reports.${ext}`,
          ext === "csv" ? "text/csv" : "application/json"
        );
      } else if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.download = `vms-reports.${filters.format || "csv"}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error("Unexpected export response from server");
      }
    } catch (err) {
      toast({ title: "Export failed", description: err?.message, status: "error" });
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <VmsPageCard title="Reports" description="Visits reports with filters, view mode, and export support.">
      <HStack mt={1} spacing={3} align="center" flexWrap="wrap">
        {isAdmin ? (
          <Select
            w={{ base: "100%", md: "200px" }}
            value={filters.office_id}
            onChange={(e) => setFilters((prev) => ({ ...prev, office_id: e.target.value }))}
          >
            <option value="">All offices</option>
            {offices.map((office) => {
              const id = office.id || office.office_id;
              return (
                <option key={id} value={id}>
                  {office.name || id}
                </option>
              );
            })}
          </Select>
        ) : null}

        <Select
          w={{ base: "100%", md: "170px" }}
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All status</option>
          <option value="REQUESTED">REQUESTED</option>
          <option value="APPROVED">APPROVED</option>
          <option value="DECLINED">DECLINED</option>
          <option value="RESCHEDULED">RESCHEDULED</option>
          <option value="SIGNED_OUT">SIGNED_OUT</option>
          <option value="EXPIRED">EXPIRED</option>
        </Select>

        <HStack spacing={2} w={{ base: "100%", md: "auto" }}>
          <Input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
          />
          <Input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
          />
        </HStack>

        <Input
          w={{ base: "100%", md: "260px" }}
          placeholder="Search visitor, meeting, office..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Button variant="outline" onClick={filtersDrawer.onOpen}>
          Filters
        </Button>
        <Button variant="outline" onClick={exportReport} isLoading={isExporting}>
          Export
        </Button>
        <Button colorScheme="green" onClick={() => loadData({ ...filters, page: 1 })} isLoading={isLoading}>
          Apply Filters
        </Button>
      </HStack>

      <TableContainer mt={4} border="1px solid" borderColor="gray.200" borderRadius="12px">
        <Table size="sm" minW="980px">
          <Thead>
            <Tr>
              <Th>Visitor</Th>
              <Th>Meeting</Th>
              <Th>Office</Th>
              <Th>Time</Th>
              <Th>Status</Th>
              <Th>Duration</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visibleRows.map((row, idx) => (
              <Tr key={row.request_code || row.id || idx} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => openDetails(row)}>
                <Td>
                  <HStack spacing={2}>
                    <Avatar
                      size="sm"
                      src={getVisitorImage(row)}
                      name={getVisitorName(row)}
                      bg="gray.100"
                      cursor={getVisitorImage(row) ? "zoom-in" : "default"}
                      onClick={(e) => {
                        e.stopPropagation();
                        openImagePreview(getVisitorImage(row));
                      }}
                    />
                    <Text fontWeight="700">{getVisitorName(row)}</Text>
                  </HStack>
                </Td>
                <Td>{getMeetingName(row)}</Td>
                <Td>{getOfficeName(row)}</Td>
                <Td>{toLocalTime(getRequestedAt(row))}</Td>
                <Td>
                  {(() => {
                    const { label, colorScheme } = getVmsStatusBadge(row.status);
                    return <Badge colorScheme={colorScheme}>{label}</Badge>;
                  })()}
                </Td>
                <Td>{getDuration(row) !== null ? `${getDuration(row)}h` : "—"}</Td>
                <Td>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDetails(row);
                    }}
                  >
                    View
                  </Button>
                </Td>
              </Tr>
            ))}
            {!visibleRows.length ? (
              <Tr>
                <Td colSpan={7}>
                  <Text py={5} textAlign="center" color="gray.500">
                    No visit records found for the selected filters.
                  </Text>
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </TableContainer>

      <HStack mt={3} justify="center" spacing={4}>
        <Button
          variant="ghost"
          onClick={() => loadData({ ...filters, page: Math.max(1, Number(meta.page || 1) - 1) })}
          isDisabled={Number(meta.page || 1) <= 1 || isLoading}
        >
          Previous
        </Button>
        <Text fontSize="sm" color="gray.600">
          {meta.page} / {meta.total_pages}
        </Text>
        <Button
          variant="ghost"
          onClick={() =>
            loadData({
              ...filters,
              page: Math.min(Number(meta.total_pages || 1), Number(meta.page || 1) + 1),
            })
          }
          isDisabled={Number(meta.page || 1) >= Number(meta.total_pages || 1) || isLoading}
        >
          Next
        </Button>
      </HStack>

      <Drawer isOpen={filtersDrawer.isOpen} placement="right" onClose={filtersDrawer.onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Advanced Filters</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Person ID</FormLabel>
                <Input
                  placeholder="Optional person_id"
                  value={filters.person_id}
                  onChange={(e) => setFilters((prev) => ({ ...prev, person_id: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>View</FormLabel>
                <Select value={filters.view} onChange={(e) => setFilters((prev) => ({ ...prev, view: e.target.value }))}>
                  <option value="lean">lean</option>
                  <option value="rich">rich</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Page Size</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={filters.page_size}
                  onChange={(e) => setFilters((prev) => ({ ...prev, page_size: Number(e.target.value || 20) }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Export Format</FormLabel>
                <Select value={filters.format} onChange={(e) => setFilters((prev) => ({ ...prev, format: e.target.value }))}>
                  <option value="csv">csv</option>
                  <option value="json">json</option>
                </Select>
              </FormControl>
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <HStack>
              <Button variant="ghost" onClick={filtersDrawer.onClose}>Close</Button>
              <Button
                colorScheme="green"
                onClick={() => {
                  filtersDrawer.onClose();
                  loadData({ ...filters, page: 1 });
                }}
                isLoading={isLoading}
              >
                Apply
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={detailsDrawer.isOpen} placement="right" onClose={detailsDrawer.onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Visit Details</DrawerHeader>
          <DrawerBody>
            {!selectedVisit ? (
              <Text color="gray.500">No visit selected.</Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <HStack spacing={3}>
                    <Avatar
                      size="md"
                      src={getVisitorImage(selectedVisit)}
                      name={getVisitorName(selectedVisit)}
                      bg="gray.100"
                      cursor={getVisitorImage(selectedVisit) ? "zoom-in" : "default"}
                      onClick={() => openImagePreview(getVisitorImage(selectedVisit))}
                    />
                    <Box>
                      <Text fontWeight="800" fontSize="lg">
                        {getVisitorName(selectedVisit)}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Request Code: {selectedVisit?.request_code || selectedVisit?.id || "-"}
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Box>
                  <Text fontWeight="800" fontSize="sm" mb={2}>
                    Visitor
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="sm"><b>Phone:</b> {getPhone(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>Email:</b> {getEmail(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>Address:</b> {getAddress(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>ID:</b> {getIdentification(selectedVisit)}</Text>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="800" fontSize="sm" mb={2}>
                    Visit
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="sm"><b>Office:</b> {getOfficeName(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>Meeting:</b> {getMeetingName(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>Purpose:</b> {getPurpose(selectedVisit)}</Text>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="800" fontSize="sm" mb={2}>
                    Timeline
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="sm"><b>Requested:</b> {toLocalTime(getRequestedAt(selectedVisit))}</Text>
                    <Text fontSize="sm"><b>Signed In:</b> {toLocalTime(getSignedInAt(selectedVisit))}</Text>
                    <Text fontSize="sm"><b>Signed Out:</b> {toLocalTime(getSignedOutAt(selectedVisit))}</Text>
                    <Text fontSize="sm">
                      <b>Signed out by:</b> {formatSignedOutBy(selectedVisit?.timestamps?.signed_out_by)}
                    </Text>
                    <Text fontSize="sm">
                      <b>Visit auto-closed:</b>{" "}
                      {selectedVisit?.timestamps?.visit_auto_closed === true
                        ? "Yes"
                        : selectedVisit?.timestamps?.visit_auto_closed === false
                          ? "No"
                          : "—"}
                    </Text>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="800" fontSize="sm" mb={2}>
                    Pass
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="sm"><b>Valid for gate entry:</b> {getPassValidForGateLabel(selectedVisit)}</Text>
                    <Text fontSize="sm"><b>Pass Generated:</b> {getPassGenerated(selectedVisit) ? "Yes" : "No"}</Text>
                    <Text fontSize="sm"><b>Pass Code:</b> {getPassCode(selectedVisit)}</Text>
                  </VStack>
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Modal isOpen={Boolean(previewImageUrl)} onClose={() => setPreviewImageUrl("")} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" />
          <ModalBody p={0}>
            <Image
              src={previewImageUrl}
              alt="Visitor preview"
              w="100%"
              maxH="80vh"
              objectFit="contain"
              borderRadius="12px"
              bg="blackAlpha.700"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VmsPageCard>
  );
};

export default VmsReportsVisits;
